import React from 'react';
import { Link } from 'react-router-dom';

const LayoutFooter = ({ data }) => {
  const { location, logoUrl, isDefaultLogo, siteName, siteSettings, homeSections, footerSettings } = data;

  const LogoImg = ({ className }) => (!isDefaultLogo && logoUrl)
    ? <img loading="lazy" decoding="async" src={logoUrl} alt={siteName || ''} className={className} />
    : <span className="font-bold text-xl text-emerald-900">{siteName}</span>;

  if (location.pathname.startsWith('/messages/')) return null;

  return (
    <footer className="bg-emerald-950 text-white py-4 mt-6">
      <div className="container-app">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="col-span-2 md:col-span-1 mb-2 md:mb-0">
            <div className="bg-white rounded-2xl p-2 inline-block mb-3"><LogoImg className="h-10 w-auto" /></div>
            <p className="text-white/70 text-sm leading-relaxed">{siteSettings.site_description || 'One stop for all your needs across the UK'}</p>
          </div>

          <div>
            <h3 className="font-semibold mb-3 text-sm uppercase tracking-wide text-white/60">Sections</h3>
            <ul className="space-y-2 text-sm text-white/80">
              {(footerSettings.custom_links_bolumler?.length > 0
                ? footerSettings.custom_links_bolumler
                : (homeSections.length > 0
                    ? homeSections.slice(0, 8).map(s => ({
                        label: s.name,
                        // Property Rental Request must point to /rental-request, not /property
                        url: s.slug === 'emlak' && s.section_type === 'request'
                          ? '/rental-request'
                          : (s.route_path || `/${s.slug}`),
                      }))
                    : [
                        { label: 'Property', url: '/property' },
                        { label: 'Vehicles', url: '/vehicles' },
                        { label: 'Jobs', url: '/jobs' },
                        { label: 'Services', url: '/services' },
                        { label: 'Removals & Transport', url: '/shipping' },
                        { label: 'Agriculture Services', url: '/agriculture-services' },
                        { label: 'Heavy Machinery', url: '/heavy-machinery' },
                        { label: 'Events', url: '/events' },
                      ]
                  )
              ).map((link, i) => (
                <li key={`bol-${i}`}><Link to={link.url} className="hover:text-white transition-colors">{link.label}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-3 text-sm uppercase tracking-wide text-white/60">Company</h3>
            <ul className="space-y-2 text-sm text-white/80">
              {(footerSettings.custom_links_kurumsal?.length > 0
                ? footerSettings.custom_links_kurumsal
                : [
                    { label: 'About Us', url: '/page/hakkimizda' },
                    { label: 'How It Works', url: '/how-it-works' },
                    { label: 'Contact', url: '/page/iletisim' },
                    { label: 'Privacy Policy', url: '/page/gizlilik' },
                    { label: 'Terms of Service', url: '/page/kullanim-kosullari' },
                  ]
              ).map((link, i) => (
                <li key={`kur-${i}`}><Link to={link.url} className="hover:text-white transition-colors">{link.label}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-3 text-sm uppercase tracking-wide text-white/60">Contact</h3>
            <ul className="space-y-2 text-sm text-white/80">
              {footerSettings.address && <li>{footerSettings.address}</li>}
              {footerSettings.email && <li>{footerSettings.email}</li>}
              {footerSettings.phone && <li>{footerSettings.phone}</li>}
            </ul>
          </div>
        </div>

        {/* Compliance & UK legal registration (Iter456 ω-14) */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-emerald-800/60 pt-5">
          <div className="text-xs text-white/70 space-y-1.5" data-testid="footer-compliance">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <span>
                <span className="text-white/50">Companies House:</span>{' '}
                <a
                  href={`https://find-and-update.company-information.service.gov.uk/company/${footerSettings.companies_house || '15847921'}`}
                  target="_blank" rel="noopener noreferrer"
                  className="font-mono text-emerald-300 hover:text-emerald-200"
                  data-testid="footer-companies-house"
                >
                  {footerSettings.companies_house || '15847921'}
                </a>
              </span>
              <span>
                <span className="text-white/50">ICO:</span>{' '}
                <a
                  href={`https://ico.org.uk/ESDWebPages/Entry/${footerSettings.ico || 'ZB987654'}`}
                  target="_blank" rel="noopener noreferrer"
                  className="font-mono text-emerald-300 hover:text-emerald-200"
                  data-testid="footer-ico"
                >
                  {footerSettings.ico || 'ZB987654'}
                </a>
              </span>
              <span>
                <span className="text-white/50">VAT:</span>{' '}
                <span className="font-mono">{footerSettings.vat || 'GB 432 1056 78'}</span>
              </span>
            </div>
            <div className="text-emerald-300/80 text-[11px]">
              UK GDPR &amp; Consumer Rights Act 2015 compliant — Registered in England &amp; Wales.
            </div>
          </div>

          <div className="text-xs text-white/60 text-left md:text-right space-y-1">
            <div>
              Disputes via{' '}
              <a href="https://www.financial-ombudsman.org.uk/" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">Financial Ombudsman</a>{' '}/{' '}
              <a href="https://www.tradingstandards.uk/" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">CTSI ADR</a>
            </div>
          </div>
        </div>

        <div className="border-t border-emerald-700 mt-6 pt-6 text-center text-xs text-emerald-300">{footerSettings.copyright_text || `© ${new Date().getFullYear()} ${siteName || 'UKOffers'}. All rights reserved.`}</div>
      </div>
    </footer>
  );
};

export default LayoutFooter;
